// src/components/Inventory/DonorsTable.js

import React, { useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTruck } from "@fortawesome/free-solid-svg-icons";
import { makeInventorySx } from "./uiStyles";
import { useTranslation } from "react-i18next";
import { getTimeAgo } from "../../utils/dateUtils";

function DonorsTable({ listings = [], requests = [], dateFrom, dateTo, materials = [] }) {
  const theme = useTheme();
  const sx = makeInventorySx(theme);
  const { t } = useTranslation();

  const rows = useMemo(() => {
    if (!Array.isArray(listings) || !Array.isArray(requests)) return [];

    const listingMap = new Map(listings.map((l) => [String(l.id), l]));
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo) : null;
    if (to) to.setHours(23, 59, 59, 999);

    const stats = new Map();

    for (const r of requests) {
      if (!r?.date_delivered) continue;

      const deliveredDate = new Date(r.date_delivered);
      if (from && deliveredDate < from) continue;
      if (to && deliveredDate > to) continue;

      const listing = listingMap.get(String(r.listing_id));
      if (!listing) continue;

      const material =
        listing.material ||
        listing.materialType ||
        listing.material_name ||
        listing.material_type;

      if (!material) continue;
      if (materials.length && !materials.includes(material)) continue;

      const donor =
        listing.donor_name ||
        listing.company_name ||
        listing.company ||
        listing.company_id ||
        "—";

      // r.quantity = allocated amount for this request
      const qty = Number(r.quantity || 0);

      const prev = stats.get(donor) || { name: donor, totalWeight: 0, pickupsCount: 0, lastDelivered: null };
      prev.totalWeight += qty;
      prev.pickupsCount += 1;
      const dStr = String(r.date_delivered).slice(0, 10);
      if (!prev.lastDelivered || dStr > prev.lastDelivered) prev.lastDelivered = dStr;
      stats.set(donor, prev);
    }

    return Array.from(stats.values()).sort((a, b) => b.totalWeight - a.totalWeight);
  }, [listings, requests, dateFrom, dateTo, materials]);

  const donorsCountLabel =
    rows.length === 1
      ? `${rows.length} ${t("donor")}`
      : `${rows.length} ${t("donors")}`;

  return (
    <Paper elevation={0} sx={sx.cardSx}>
      <Box sx={sx.cardHeaderSx}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
            {t("Donors")}
          </Typography>
          <Typography variant="caption" sx={sx.captionSx}>
            {t("Ranked by total delivered weight")}
          </Typography>
        </Box>

        <Chip
          size="small"
          variant="outlined"
          label={donorsCountLabel}
          sx={sx.chipSx}
        />
      </Box>

      <TableContainer>
        <Table
          size="small"
          sx={{
            ...sx.tableSx,
            tableLayout: "fixed",
            width: "100%",
          }}
        >
          <colgroup>
            <col style={{ width: "40%" }} />
            <col style={{ width: "24%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "24%" }} />
          </colgroup>

          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{t("Donor")}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                {t("Total Weight (lbs)")}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                {t("Pickups")}
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                {t("Last Delivery")}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody sx={sx.zebraBodySx}>
            {rows.map((row) => {
              const ago = getTimeAgo(row.lastDelivered);
              return (
                <TableRow key={row.name}>
                  <TableCell sx={{ fontWeight: 700 }}>{row.name}</TableCell>
                  <TableCell align="right" sx={sx.numberCellSx}>
                    {row.totalWeight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell align="right" sx={sx.numberCellSx}>
                    {row.pickupsCount}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FontAwesomeIcon icon={faTruck} style={{ fontSize: "0.75rem", color: "#669d2e", opacity: 0.7 }} />
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ fontWeight: 600, fontSize: "0.8rem" }}>{ago || row.lastDelivered}</span>
                        {ago && <span style={{ fontSize: "0.7rem", opacity: 0.6 }}>{row.lastDelivered}</span>}
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}

            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3, opacity: 0.75 }}>
                  {t("No confirmed pickups in selected period.")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>

          {rows.length > 0 && (
            <TableBody>
              <TableRow sx={{ "& td": { borderBottom: "none", fontWeight: 700 } }}>
                <TableCell>{t("Total")}</TableCell>
                <TableCell align="right" sx={sx.numberCellSx}>
                  {rows.reduce((s, r) => s + r.totalWeight, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
                <TableCell align="right" sx={sx.numberCellSx}>
                  {rows.reduce((s, r) => s + r.pickupsCount, 0)}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default DonorsTable;
